import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { devicesApi, schoolsApi, roomsApi } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/common/StatusIndicator';
import { Plus, Search, Filter, Cpu, Radio, Wifi, Pencil, Trash2, Loader2, ArrowRightLeft, MoveRight, RotateCcw, Network, Copy, Tag, Box } from 'lucide-react';
import { format } from 'date-fns';
import { cn, utcDate } from '@/lib/utils';
import { SchoolSelector } from '@/components/SchoolSelector';
import { NetworkExportDialog } from '@/components/NetworkExportDialog';
import { PackagingLabelsDialog } from '@/components/PackagingLabelsDialog';
import { ReplaceAttrappDialog } from '@/components/ReplaceAttrappDialog';

const CONN = {
  online: { label: 'Online', dot: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200' },
  warning: { label: 'Driftvarning', dot: 'bg-amber-500', text: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
  offline: { label: 'Offline', dot: 'bg-slate-400', text: 'text-slate-600', bg: 'bg-slate-100 border-slate-200' },
  never_activated: { label: 'Väntar på data', dot: 'bg-sky-400', text: 'text-sky-700', bg: 'bg-sky-50 border-sky-200' },
  placeholder: { label: 'Attrapp', dot: 'bg-amber-400', text: 'text-amber-800', bg: 'bg-amber-50 border-amber-200' },
};

function ConnectivityBadge({ status }) {
  const c = CONN[status] || CONN.offline;
  return (
    <span className={cn("inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border font-ibm whitespace-nowrap", c.bg, c.text)} data-testid="connectivity-badge">
      <span className={cn("h-2 w-2 rounded-full", c.dot)} />{c.label}
    </span>
  );
}

function relLastSeen(ts) {
  if (!ts) return 'Aldrig';
  const mins = Math.max(0, Math.floor((Date.now() - utcDate(ts).getTime()) / 60000));
  if (mins < 1) return 'nyss';
  if (mins < 60) return `för ${mins} min sedan`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `för ${h} tim sedan`;
  const d = Math.floor(h / 24);
  return `för ${d} dygn sedan`;
}

function ConnectionTypeBadge({ type }) {
  if (type === 'wifi') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700" data-testid="badge-wifi">
        <Wifi className="h-3 w-3" />WiFi
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700" data-testid="badge-lorawan">
      <Radio className="h-3 w-3" />LoRaWAN
    </span>
  );
}

const EMPTY_FORM = { device_eui: '', name: '', device_type: 'FS00802G', room_id: '', connection_type: 'lorawan', device_model: '' };

export default function DevicesPage() {
  const { isSuperAdmin, isKommunAdmin, isSchoolAdmin } = useAuth();
  const canExportNetwork = isSuperAdmin();
  const [devices, setDevices] = useState([]);
  const [schools, setSchools] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [connectionFilter, setConnectionFilter] = useState('all');
  const [showAttrapper, setShowAttrapper] = useState(false);
  const [replaceDevice, setReplaceDevice] = useState(null);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createStep, setCreateStep] = useState('type'); // 'type' | 'form'
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editDevice, setEditDevice] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', room_id: '', is_active: true, wifi_mac_address: '', serial_number: '', firmware_version: '' });

  // Network document export dialog
  const [exportOpen, setExportOpen] = useState(false);
  // Packaging labels dialog
  const [labelsOpen, setLabelsOpen] = useState(false);

  // Attrapp (placeholder) dialog
  const [attrappOpen, setAttrappOpen] = useState(false);
  const [attrappSchool, setAttrappSchool] = useState('');
  const [attrappRoom, setAttrappRoom] = useState('');
  const [attrappName, setAttrappName] = useState('');
  const [attrappConn, setAttrappConn] = useState('wifi');
  const [attrappSaving, setAttrappSaving] = useState(false);

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteDevice, setDeleteDevice] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Move dialog
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveDevice, setMoveDevice] = useState(null);
  const [moveRoomId, setMoveRoomId] = useState('');
  const [moving, setMoving] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [devR, schR, rmR] = await Promise.all([devicesApi.list({}), schoolsApi.list(), roomsApi.list()]);
      setDevices(devR.data);
      setSchools(schR.data);
      setRooms(rmR.data);
    } catch { toast.error('Kunde inte ladda enheter'); }
    finally { setLoading(false); }
  };

  // --- Create ---
  const openCreate = (type) => {
    const connType = type === 'wifi' ? 'wifi' : 'lorawan';
    const devType = type === 'wifi' ? 'AirPoint-1' : 'FS00802G';
    setFormData({ ...EMPTY_FORM, connection_type: connType, device_type: devType });
    setCreateStep('form');
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.device_eui.trim() || !formData.room_id) {
      toast.error('EUI och rum krävs');
      return;
    }
    setSaving(true);
    try {
      await devicesApi.create(formData);
      toast.success('Enhet skapad');
      setCreateOpen(false);
      setCreateStep('type');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa enhet');
    } finally { setSaving(false); }
  };

  const openAttrapp = () => {
    setAttrappSchool(schools.length === 1 ? schools[0].id : '');
    setAttrappRoom(''); setAttrappName(''); setAttrappConn('wifi');
    setAttrappOpen(true);
  };

  const handleCreateAttrapp = async () => {
    if (!attrappRoom) { toast.error('Välj rum'); return; }
    setAttrappSaving(true);
    try {
      await devicesApi.createPlaceholder({ room_id: attrappRoom, name: attrappName.trim() || null, connection_type: attrappConn });
      toast.success('Attrapp skapad');
      setShowAttrapper(true);
      setAttrappOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte skapa attrapp');
    } finally { setAttrappSaving(false); }
  };

  // --- Edit ---
  const openEdit = (dev, e) => {
    e.preventDefault();
    e.stopPropagation();
    setEditDevice(dev);
    setEditForm({
      name: dev.name || '', room_id: dev.room_id || '', is_active: dev.is_active,
      wifi_mac_address: dev.wifi_mac_address || '', serial_number: dev.serial_number || '',
      firmware_version: dev.firmware_version || '',
    });
    setEditOpen(true);
  };

  const openMove = (dev, e) => {
    e.preventDefault();
    e.stopPropagation();
    setMoveDevice(dev);
    setMoveRoomId('');
    setMoveOpen(true);
  };

  const handleEdit = async () => {
    if (!editDevice) return;
    setSaving(true);
    try {
      const payload = {};
      if (editForm.name !== editDevice.name) payload.name = editForm.name;
      if (editForm.room_id !== editDevice.room_id) payload.room_id = editForm.room_id;
      if (editForm.is_active !== editDevice.is_active) payload.is_active = editForm.is_active;
      if ((editForm.wifi_mac_address || '') !== (editDevice.wifi_mac_address || '')) payload.wifi_mac_address = editForm.wifi_mac_address.trim();
      if ((editForm.serial_number || '') !== (editDevice.serial_number || '')) payload.serial_number = editForm.serial_number.trim();
      if ((editForm.firmware_version || '') !== (editDevice.firmware_version || '')) payload.firmware_version = editForm.firmware_version.trim();
      if (Object.keys(payload).length === 0) {
        setEditOpen(false);
        return;
      }
      await devicesApi.update(editDevice.id, payload);
      toast.success(payload.room_id ? 'Enhet flyttad till nytt rum' : 'Enhet uppdaterad');
      setEditOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte uppdatera');
    } finally { setSaving(false); }
  };

  // --- Delete ---
  const openDelete = (dev, e) => {
    e.preventDefault();
    e.stopPropagation();
    setDeleteDevice(dev);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteDevice) return;
    setDeleting(true);
    try {
      await devicesApi.delete(deleteDevice.id);
      toast.success('Enhet borttagen');
      setDeleteOpen(false);
      setDeleteDevice(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte ta bort');
    } finally { setDeleting(false); }
  };

  const handleMove = async () => {
    if (!moveDevice || !moveRoomId) return;
    setMoving(true);
    try {
      const res = await devicesApi.move(moveDevice.id, moveRoomId);
      toast.success(res.data.message);
      setMoveOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte flytta');
    } finally { setMoving(false); }
  };

  const handleResetCalibration = async (dev, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Nollställ ML-kalibrering för ${dev.name || dev.device_eui}?\n\nBaslinje, decay-monitor och kalibreringscache rensas. Sensorn börjar om med inlärning.`)) return;
    try {
      const res = await devicesApi.resetCalibration(dev.id);
      toast.success(res.data.message);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunde inte nollställa');
    }
  };

  const attrappCount = devices.filter(d => d.is_placeholder).length;
  const filtered = devices.filter((d) => {
    if (d.is_placeholder && !showAttrapper) return false;
    const matchSearch = d.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.device_eui.toLowerCase().includes(searchQuery.toLowerCase()) ||
      d.room_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchStatus = statusFilter === 'all' || (d.connectivity_status || 'offline') === statusFilter;
    const matchSchool = schoolFilter === 'all' || d.school_name === schools.find(s => s.id === schoolFilter)?.name;
    const matchConn = connectionFilter === 'all' || d.connection_type === connectionFilter;
    return matchSearch && matchStatus && matchSchool && matchConn;
  });

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="devices-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-chivo font-black text-2xl md:text-3xl text-navy tracking-tight">Enheter</h1>
          <p className="text-slate-500 mt-1 font-ibm">Hantera sensorer — lägg till, flytta eller ta bort</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canExportNetwork && (
            <Button
              variant="outline"
              onClick={() => setLabelsOpen(true)}
              className="font-ibm gap-2"
              data-testid="packaging-labels-btn"
            >
              <Tag className="h-4 w-4" />
              Förpackningsetiketter
            </Button>
          )}
          {canExportNetwork && (
            <Button
              variant="outline"
              onClick={() => setExportOpen(true)}
              className="font-ibm gap-2"
              data-testid="export-network-btn"
            >
              <Network className="h-4 w-4" />
              Exportera nätverksunderlag
            </Button>
          )}
          {isSuperAdmin() && (
            <>
              <Button
                variant="outline"
                onClick={openAttrapp}
                className="font-ibm gap-2"
                data-testid="add-attrapp-btn"
              >
                <Box className="h-4 w-4" />
                Lägg till attrapp
              </Button>
              <Button
                onClick={() => { setCreateStep('type'); setCreateOpen(true); }}
                className="bg-navy hover:bg-navy/90 rounded-sm font-ibm gap-2"
                data-testid="add-device-btn"
              >
                <Plus className="h-4 w-4" />
                Lagg till enhet
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Sök enheter..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="pl-9 bg-white border-slate-200" data-testid="device-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[150px] bg-white" data-testid="status-filter">
            <Filter className="h-4 w-4 mr-2" /><SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla statusar</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="warning">Driftvarning</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
            <SelectItem value="never_activated">Väntar på data</SelectItem>
          </SelectContent>
        </Select>
        <SchoolSelector schools={schools} value={schoolFilter} onChange={setSchoolFilter} triggerClassName="w-full sm:w-[180px] bg-white" />
        <Select value={connectionFilter} onValueChange={setConnectionFilter}>
          <SelectTrigger className="w-full sm:w-[160px] bg-white" data-testid="connection-filter">
            <SelectValue placeholder="Anslutningstyp" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla typer</SelectItem>
            <SelectItem value="lorawan">LoRaWAN</SelectItem>
            <SelectItem value="wifi">WiFi</SelectItem>
          </SelectContent>
        </Select>
        {attrappCount > 0 && (
          <Button variant={showAttrapper ? 'default' : 'outline'} onClick={() => setShowAttrapper(v => !v)}
            className={cn("font-ibm gap-2 whitespace-nowrap", showAttrapper && "bg-amber-500 hover:bg-amber-600 text-white")}
            data-testid="toggle-attrapper-btn">
            <Box className="h-4 w-4" />
            {showAttrapper ? 'Dölj attrapper' : 'Visa attrapper'} ({attrappCount})
          </Button>
        )}
      </div>

      {/* Devices Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map(device => (
          <Link key={device.id} to={`/devices/${device.id}`} className="block" data-testid={`device-card-${device.id}`}>
            <Card className="border-slate-200 hover:border-slate-300 transition-colors h-full group">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className={cn("h-10 w-10 rounded-sm flex items-center justify-center",
                      device.connection_type === 'wifi' ? 'bg-purple-100' : 'bg-slate-100')}>
                      {device.connection_type === 'wifi'
                        ? <Wifi className="h-5 w-5 text-purple-600" />
                        : <Cpu className="h-5 w-5 text-slate-600" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-ibm font-semibold text-slate-900">{device.name || device.device_eui}</h3>
                        <ConnectionTypeBadge type={device.connection_type} />
                        {device.is_placeholder && (
                          <span className="inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-ibm font-semibold" data-testid="badge-attrapp">
                            <Box className="h-3 w-3" />ATTRAPP
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 font-mono">{device.device_eui}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <ConnectivityBadge status={device.connectivity_status} />
                    {isSuperAdmin() && (
                      <div className="flex opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => openMove(device, e)} data-testid={`move-device-${device.id}`} title="Flytta till annat rum">
                          <MoveRight className="h-3.5 w-3.5 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => handleResetCalibration(device, e)} data-testid={`reset-calibration-${device.id}`} title="Nollställ ML-kalibrering">
                          <RotateCcw className="h-3.5 w-3.5 text-amber-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={e => openEdit(device, e)} data-testid={`edit-device-${device.id}`}>
                          <Pencil className="h-3.5 w-3.5 text-slate-400" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-600" onClick={e => openDelete(device, e)} data-testid={`delete-device-${device.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Skola</span>
                    <span className="font-ibm text-slate-700">{device.school_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Rum</span>
                    <span className="font-ibm text-slate-700">{device.room_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Senaste kontakt</span>
                    <span className="font-ibm text-slate-700 tabular-nums" data-testid={`last-contact-${device.id}`}>
                      {relLastSeen(device.last_seen)}
                    </span>
                  </div>
                  {device.connectivity_status === 'warning' && (
                    <p className="text-xs text-amber-600 font-ibm pt-0.5" data-testid={`warning-hint-${device.id}`}>
                      Kommunikationen med sensorn har avbrutits. Kontrollera ström och nätverksanslutning.
                    </p>
                  )}
                  {device.is_placeholder && (
                    <p className="text-xs text-amber-700 font-ibm pt-0.5">
                      Attrapp – ingen riktig sensor. Larmar aldrig och ger inga offline-varningar.
                    </p>
                  )}
                  {device.is_placeholder && isSuperAdmin() && (
                    <Button size="sm" variant="outline" className="w-full mt-1 font-ibm gap-2 border-amber-300 text-amber-800 hover:bg-amber-50"
                      onClick={e => { e.preventDefault(); e.stopPropagation(); setReplaceDevice(device); }}
                      data-testid={`replace-attrapp-${device.id}`}>
                      <ArrowRightLeft className="h-3.5 w-3.5" />Ersätt med riktig sensor
                    </Button>
                  )}
                  {device.connectivity_status === 'never_activated' && (
                    <p className="text-xs text-sky-600 font-ibm pt-0.5">
                      Sensor registrerad men ännu ingen data mottagen.
                    </p>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-500">Poäng</span>
                    <span className="font-ibm font-semibold tabular-nums">{(device.last_score * 100).toFixed(0)}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Baslinje</span>
                    <span className={`font-ibm text-xs ${device.baseline_pm1 != null ? 'text-emerald-600' : 'text-amber-500'}`}>
                      {device.baseline_pm1 != null ? 'Kalibrerad' : 'Ej kalibrerad'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {filtered.length === 0 && (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Cpu className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-chivo text-lg text-slate-700">Inga enheter hittades</h3>
            <p className="text-slate-500 mt-1 font-ibm">
              {devices.length === 0 ? 'Lägg till en enhet för att börja övervaka' : 'Prova att justera dina filter'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog — Step 1: Choose type */}
      <Dialog open={createOpen} onOpenChange={(v) => { setCreateOpen(v); if (!v) setCreateStep('type'); }}>
        <DialogContent className="sm:max-w-md" data-testid="create-device-dialog">
          {createStep === 'type' ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-chivo">Lägg till enhet</DialogTitle>
                <DialogDescription className="font-ibm">Välj anslutningstyp för den nya sensorn</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 py-4">
                <button
                  onClick={() => openCreate('lorawan')}
                  className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 hover:border-sky-400 hover:bg-sky-50 transition-all group"
                  data-testid="create-lorawan-btn"
                >
                  <div className="h-14 w-14 rounded-full bg-sky-100 group-hover:bg-sky-200 flex items-center justify-center transition-colors">
                    <Radio className="h-7 w-7 text-sky-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-ibm font-semibold text-slate-900">LoRaWAN</p>
                    <p className="text-xs text-slate-500 mt-1">TTN-ansluten sensor</p>
                  </div>
                </button>
                <button
                  onClick={() => openCreate('wifi')}
                  className="flex flex-col items-center gap-3 p-6 rounded-lg border-2 border-slate-200 hover:border-purple-400 hover:bg-purple-50 transition-all group"
                  data-testid="create-wifi-btn"
                >
                  <div className="h-14 w-14 rounded-full bg-purple-100 group-hover:bg-purple-200 flex items-center justify-center transition-colors">
                    <Wifi className="h-7 w-7 text-purple-600" />
                  </div>
                  <div className="text-center">
                    <p className="font-ibm font-semibold text-slate-900">WiFi</p>
                    <p className="text-xs text-slate-500 mt-1">MQTT-ansluten sensor</p>
                  </div>
                </button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="font-chivo flex items-center gap-2">
                  {formData.connection_type === 'wifi'
                    ? <><Wifi className="h-5 w-5 text-purple-600" /> WiFi-sensor</>
                    : <><Radio className="h-5 w-5 text-sky-600" /> LoRaWAN-sensor</>}
                </DialogTitle>
                <DialogDescription className="font-ibm">
                  {formData.connection_type === 'wifi'
                    ? 'Sensorn ansluter via WiFi/MQTT-broker'
                    : 'Sensorn ansluter via The Things Network (TTN)'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest">
                    {formData.connection_type === 'wifi' ? 'Sensor-ID (MQTT topic)' : 'Enhets-EUI'}
                  </Label>
                  <Input value={formData.device_eui} onChange={e => setFormData(f => ({ ...f, device_eui: e.target.value }))}
                    placeholder={formData.connection_type === 'wifi' ? 'sensor001' : 'A81758FFFE123456'}
                    required className="bg-slate-50 font-mono" data-testid="device-eui-input" />
                  <p className="text-xs text-slate-400">
                    {formData.connection_type === 'wifi'
                      ? 'Sensor-ID som matchar MQTT-topic (t.ex. sensor001)'
                      : 'DevEUI från sensorns etikett (16 hex-tecken)'}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest">Enhetsnamn</Label>
                  <Input value={formData.name} onChange={e => setFormData(f => ({ ...f, name: e.target.value }))}
                    placeholder="Toalettsensor 1" className="bg-slate-50" data-testid="device-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="font-ibm text-xs uppercase tracking-widest">Rum *</Label>
                  <Select value={formData.room_id} onValueChange={v => setFormData(f => ({ ...f, room_id: v }))}>
                    <SelectTrigger className="bg-slate-50" data-testid="device-room-select">
                      <SelectValue placeholder="Välj ett rum" />
                    </SelectTrigger>
                    <SelectContent>
                      {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setCreateStep('type')} className="font-ibm">Tillbaka</Button>
                  <Button type="submit" disabled={saving} className="bg-navy hover:bg-navy/90 font-ibm gap-2 flex-1" data-testid="device-submit-btn">
                    {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                    Skapa enhet
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-md" data-testid="edit-device-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Redigera enhet</DialogTitle>
            <DialogDescription className="font-ibm">
              {editDevice?.device_eui} — {editDevice?.connection_type === 'wifi' ? 'WiFi' : 'LoRaWAN'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Enhetsnamn</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                data-testid="edit-device-name" />
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest flex items-center gap-2">
                <ArrowRightLeft className="h-3.5 w-3.5" /> Flytta till rum
              </Label>
              <Select value={editForm.room_id} onValueChange={v => setEditForm(f => ({ ...f, room_id: v }))}>
                <SelectTrigger data-testid="edit-device-room">
                  <SelectValue placeholder="Välj rum" />
                </SelectTrigger>
                <SelectContent>
                  {rooms.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {editForm.room_id !== editDevice?.room_id && (
                <p className="text-xs text-amber-600 font-ibm flex items-center gap-1">
                  <ArrowRightLeft className="h-3 w-3" />
                  Enheten flyttas från "{editDevice?.room_name}" till "{rooms.find(r => r.id === editForm.room_id)?.name}"
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="font-ibm text-xs uppercase tracking-widest">Wi-Fi MAC-adress</Label>
              <div className="flex gap-2">
                <Input value={editForm.wifi_mac_address}
                  onChange={e => setEditForm(f => ({ ...f, wifi_mac_address: e.target.value }))}
                  placeholder="AA:BB:CC:DD:EE:FF" className="font-mono" data-testid="edit-device-mac" />
                <Button type="button" variant="outline" size="icon" title="Kopiera MAC"
                  disabled={!editForm.wifi_mac_address}
                  onClick={async () => {
                    try { await navigator.clipboard.writeText(editForm.wifi_mac_address); toast.success('MAC-adress kopierad'); }
                    catch { toast.error('Kunde inte kopiera'); }
                  }}
                  data-testid="edit-device-mac-copy">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400">Wi-Fi MAC (ej Bluetooth/LoRaWAN DevEUI). Format AA:BB:CC:DD:EE:FF.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Serienummer</Label>
                <Input value={editForm.serial_number}
                  onChange={e => setEditForm(f => ({ ...f, serial_number: e.target.value }))}
                  placeholder="VL2026XXXX" data-testid="edit-device-serial" />
              </div>
              <div className="space-y-2">
                <Label className="font-ibm text-xs uppercase tracking-widest">Firmware</Label>
                <Input value={editForm.firmware_version}
                  onChange={e => setEditForm(f => ({ ...f, firmware_version: e.target.value }))}
                  placeholder="t.ex. 1.4.2" data-testid="edit-device-firmware" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="font-ibm">Avbryt</Button>
            <Button onClick={handleEdit} disabled={saving} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="edit-device-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Spara
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-device-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Ta bort enhet</DialogTitle>
            <DialogDescription className="font-ibm">
              Är du säker på att du vill ta bort <strong>{deleteDevice?.name || deleteDevice?.device_eui}</strong>?
              All sensordata och händelser kopplade till enheten raderas.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOpen(false)} className="font-ibm">Avbryt</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} className="font-ibm gap-2" data-testid="confirm-delete-device">
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
              Ta bort
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move Dialog */}
      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="move-device-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo">Flytta sensor</DialogTitle>
            <DialogDescription className="font-ibm">
              Flytta <strong>{moveDevice?.name}</strong> till ett annat rum. Baslinjen nollställs automatiskt.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <label className="text-sm font-ibm font-medium text-slate-700 block mb-1.5">Nytt rum</label>
            <Select value={moveRoomId} onValueChange={setMoveRoomId}>
              <SelectTrigger data-testid="move-room-select">
                <SelectValue placeholder="Välj rum..." />
              </SelectTrigger>
              <SelectContent>
                {rooms.filter(r => r.id !== moveDevice?.room_id).map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    {r.name} — {schools.find(s => s.id === r.school_id)?.name || ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)} className="font-ibm">Avbryt</Button>
            <Button onClick={handleMove} disabled={!moveRoomId || moving} className="font-ibm gap-2" data-testid="confirm-move-device">
              {moving && <Loader2 className="h-4 w-4 animate-spin" />}
              <MoveRight className="h-4 w-4" />
              Flytta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attrapp (placeholder) Dialog */}
      <Dialog open={attrappOpen} onOpenChange={setAttrappOpen}>
        <DialogContent className="sm:max-w-md" data-testid="attrapp-dialog">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <Box className="h-5 w-5 text-amber-600" /> Lägg till attrapp
            </DialogTitle>
            <DialogDescription className="font-ibm">
              Skapar en tom placeholder-sensor (ATTRAPP) i ett rum. Den larmar aldrig och kan senare kopplas till en riktig sensor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Skola</Label>
              <Select value={attrappSchool} onValueChange={(v) => { setAttrappSchool(v); setAttrappRoom(''); }}>
                <SelectTrigger data-testid="attrapp-school-select"><SelectValue placeholder="Välj skola…" /></SelectTrigger>
                <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Rum</Label>
              <Select value={attrappRoom} onValueChange={setAttrappRoom} disabled={!attrappSchool}>
                <SelectTrigger data-testid="attrapp-room-select"><SelectValue placeholder={attrappSchool ? 'Välj rum…' : 'Välj skola först'} /></SelectTrigger>
                <SelectContent>
                  {rooms.filter(r => r.school_id === attrappSchool).map(r => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Anslutningstyp</Label>
              <Select value={attrappConn} onValueChange={setAttrappConn}>
                <SelectTrigger data-testid="attrapp-conn-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="wifi">WiFi</SelectItem>
                  <SelectItem value="lorawan">LoRaWAN</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="font-ibm text-xs uppercase tracking-widest text-slate-500">Namn (valfritt)</Label>
              <Input
                value={attrappName}
                onChange={e => setAttrappName(e.target.value)}
                placeholder="Lämnas tomt → rummets namn"
                data-testid="attrapp-name-input"
              />
              <p className="text-[11px] text-slate-400 font-ibm">Namnet prefixas alltid med "ATTRAPP" så den syns tydligt i systemet.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttrappOpen(false)} className="font-ibm">Avbryt</Button>
            <Button onClick={handleCreateAttrapp} disabled={!attrappRoom || attrappSaving} className="bg-navy hover:bg-navy/90 font-ibm gap-2" data-testid="confirm-create-attrapp">
              {attrappSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Box className="h-4 w-4" />}
              Skapa attrapp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Network document export */}
      <NetworkExportDialog open={exportOpen} onOpenChange={setExportOpen} schools={schools} />

      {/* Packaging labels */}
      <PackagingLabelsDialog open={labelsOpen} onOpenChange={setLabelsOpen} schools={schools} devices={devices} />

      <ReplaceAttrappDialog device={replaceDevice} onClose={() => setReplaceDevice(null)}
        onDone={() => { setReplaceDevice(null); loadData(); }} />
    </div>
  );
}
